import { z } from 'zod';
import { NotificationType, NotificationPriority, NotificationChannel } from './notification.enums';
import { PaginationParamsSchema, PaginationResponseSchema } from '../common/common.schemas';
import { NotificationCategory } from '../user';

/** Inbox the row belongs to: `null` = viewer inbox, uuid = that host or vendor profile's inbox. */
const NotificationProfileIdSchema = z.uuid().nullable();

export const NotificationResponseSchema = z.object({
  id: z.uuid(),
  userId: z.uuid(),
  profileId: NotificationProfileIdSchema,
  type: z.enum(NotificationType),
  title: z.string(),
  body: z.string(),
  actionUrl: z.url().optional(),
  actionType: z.string().optional(),
  channels: z.array(z.enum(NotificationChannel)),
  isRead: z.boolean(),
  readAt: z.iso.datetime().optional(),
  deliveredAt: z.iso.datetime().optional(),
  priority: z.enum(NotificationPriority),
  metadata: z.record(z.string(), z.any()).optional(),
  createdAt: z.iso.datetime(),
});

const EXPO_TOKEN_REGEX = /^ExponentPushToken\[[A-Za-z0-9_-]{18,32}\]$/;

export const RegisterPushDeviceRequestSchema = z.object({
  expoPushToken: z.string().regex(EXPO_TOKEN_REGEX),
  platform: z.enum(['IOS', 'ANDROID', 'UNKNOWN']),
  appVersion: z.string().max(24).optional(),
  deviceLabel: z.string().max(80).optional(),
});

export const RegisterPushDeviceResponseSchema = z.object({
  deviceId: z.string(),
  registeredAt: z.iso.datetime(),
});

/** `GET /notifications` — paged in-app notification inbox for the authenticated user. */
export const ListNotificationsQuerySchema = PaginationParamsSchema.extend({
  profileId: z.uuid().optional(),
  isRead: z
    .enum(['true', 'false'])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === 'true')),
  /**
   * Case-insensitive match on title, body, or type
   * (UI: search by role, name, or status copy).
   */
  search: z.string().trim().min(2).max(100).optional(),
});

/** Shared optional inbox scope for mark-all / clear-all. */
export const NotificationInboxScopeQuerySchema = z.object({
  profileId: z.uuid().optional(),
});

export const ListNotificationsResponseSchema = z.object({
  items: z.array(NotificationResponseSchema),
  ...PaginationResponseSchema.shape,
});

export const NotificationsUnreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

export const MarkNotificationReadResponseSchema = z.object({
  id: z.uuid(),
  isRead: z.literal(true),
  readAt: z.iso.datetime(),
});

export const MarkAllNotificationsReadResponseSchema = z.object({
  markedCount: z.number().int().nonnegative(),
  /** True when more unread rows remain beyond the interactive write bound. */
  hasMore: z.boolean().optional(),
});

export const ClearAllNotificationsResponseSchema = z.object({
  clearedCount: z.number().int().nonnegative(),
  /** True when more rows remain beyond the interactive write bound. */
  hasMore: z.boolean().optional(),
});

/** Server → client event names on the `/notifications` Socket.IO namespace. */
export const NotificationSocketEvents = {
  Connected: 'notification.connected',
  Created: 'notification.created',
  Updated: 'notification.updated',
  Read: 'notification.read',
  ReadAll: 'notification.read_all',
  Cleared: 'notification.cleared',
  UnreadCounts: 'notification.unread_counts',
} as const;

export const NotificationConnectedPayloadSchema = z.object({
  userId: z.uuid(),
});

export const NotificationCreatedPayloadSchema = z.object({
  notification: NotificationResponseSchema,
});

/** A recent unread row was refreshed in place (coalesced burst) — upsert by `notification.id`. */
export const NotificationUpdatedPayloadSchema = z.object({
  notification: NotificationResponseSchema,
});

export const NotificationReadPayloadSchema = z.object({
  id: z.uuid(),
  profileId: NotificationProfileIdSchema,
  readAt: z.iso.datetime(),
});

export const NotificationReadAllPayloadSchema = z.object({
  profileId: NotificationProfileIdSchema,
  markedCount: z.number().int().nonnegative(),
  readAt: z.iso.datetime(),
});

export const NotificationClearedPayloadSchema = z.object({
  profileId: NotificationProfileIdSchema,
  clearedCount: z.number().int().nonnegative(),
  clearedAt: z.iso.datetime(),
});

/** Full snapshot of unread badges across the user's inboxes; an inbox not listed has 0 unread. */
export const NotificationUnreadCountsPayloadSchema = z.object({
  counts: z.array(
    z.object({
      profileId: NotificationProfileIdSchema,
      count: z.number().int().positive(),
    }),
  ),
});

const HOST_CATEGORY = z.enum([
  NotificationCategory.EVENT_REMINDERS,
  NotificationCategory.TICKET_PURCHASES,
  NotificationCategory.CREW_UPDATES,
]);
const VENDOR_CATEGORY = z.enum([
  NotificationCategory.JOB_INVITATIONS,
  NotificationCategory.JOB_CONFIRMATIONS,
  NotificationCategory.MESSAGES,
  NotificationCategory.PAYMENT_ALERTS,
]);

export const HostNotificationPreferenceItemSchema = z.object({
  category: HOST_CATEGORY,
  enabled: z.boolean(),
});

export const VendorNotificationPreferenceItemSchema = z.object({
  category: VENDOR_CATEGORY,
  enabled: z.boolean(),
});

export const HostNotificationPreferencesResponseSchema = z.object({
  preferences: z.record(HOST_CATEGORY, z.boolean()),
});

export const VendorNotificationPreferencesResponseSchema = z.object({
  preferences: z.record(VENDOR_CATEGORY, z.boolean()),
});

export const UpdateHostNotificationPreferencesRequestSchema = z.object({
  preferences: z.array(HostNotificationPreferenceItemSchema).min(1).max(10),
});

export const UpdateVendorNotificationPreferencesRequestSchema = z.object({
  preferences: z.array(VendorNotificationPreferenceItemSchema).min(1).max(10),
});
